import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Typography, 
  Select, 
  DatePicker, 
  InputNumber, 
  Row, 
  Col, 
  Divider, 
  message,
  Spin,
  Alert
} from 'antd';
import { 
  ClockCircleOutlined, 
  ProjectOutlined, 
  CalendarOutlined, 
  CheckSquareOutlined,
  SaveOutlined,
  ClearOutlined
} from '@ant-design/icons';
import { useAuthStore } from '../../store/useAuthStore';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const NewTimesheetForm = () => {
  const [form] = Form.useForm();
  const { user } = useAuthStore();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [error, setError] = useState(null);

  // Fetch projects on component mount
  useEffect(() => {
    if (user?.id) {
      fetchProjects();
      // Set initial form values
      form.setFieldsValue({
        employeeName: user.name,
        employeeEmail: user.email,
        date: dayjs(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, form]);

  // Fetch projects based on user role
  const fetchProjects = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const endpoint = user.role === 'employee' || user.role === 'project_manager' 
        ? `/api/projects/employee/${user.id}`
        : '/api/projects';
      
      const response = await fetch(endpoint);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch projects: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setProjects(data.data);
      } else {
        setProjects([]);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to load projects. Please refresh the page.');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch tasks when project is selected
  const fetchTasks = async (projectId) => {
    if (!projectId) {
      setTasks([]);
      return;
    }

    try {
      const response = await fetch(`/api/tasks/project/${projectId}?status=pending&status=in-progress`);
      
      if (response.ok) {
        const data = await response.json();
        // Filter tasks assigned to current user
        const userTasks = Array.isArray(data) 
          ? data.filter(task => task.assignedTo?._id === user.id)
          : [];
        setTasks(userTasks);
      } else {
        console.warn('Failed to fetch tasks');
        setTasks([]);
      }
    } catch (error) {
      console.warn('Error fetching tasks:', error);
      setTasks([]);
    }
  };

  // Handle project selection
  const handleProjectChange = (projectId) => {
    setSelectedProject(projectId);
    form.setFieldsValue({ task: undefined }); // Clear task selection
    fetchTasks(projectId);
  };

  // Handle form submission
  const handleSubmit = async (values) => {
    setSubmitting(true);
    setError(null);

    try {
      // Validate required fields
      if (!values.project || !values.date || !values.hours) {
        throw new Error('Please fill in all required fields');
      }

      // Find the selected project
      const selectedProjectData = projects.find(p => p._id === values.project);
      if (!selectedProjectData) {
        throw new Error('Selected project not found');
      }

      // Find selected task if provided
      const selectedTask = values.task ? tasks.find(t => t._id === values.task) : null;

      // Prepare timesheet data
      const timesheetData = {
        employeeId: user.id,
        employeeName: user.name,
        employeeEmail: user.email,
        projectId: values.project,
        projectName: selectedProjectData.name,
        taskId: values.task || null,
        taskTitle: selectedTask?.title || '',
        date: values.date.format('YYYY-MM-DD'),
        hours: Number(values.hours),
        description: values.description?.trim() || '',
      };

      // Submit timesheet
      const response = await fetch('/api/timesheets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(timesheetData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Submission failed: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Submission failed');
      }

      // Update task actual hours if task is selected
      if (values.task && values.hours && selectedTask) {
        try {
          await fetch(`/api/tasks/${values.task}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              actualHours: (selectedTask.actualHours || 0) + Number(values.hours)
            }),
          });
        } catch (taskError) {
          console.warn('Failed to update task hours:', taskError);
          // Don't fail the submission for this
        }
      }

      // Success - reset form
      message.success('Timesheet submitted successfully!');
      handleClearForm();

    } catch (err) {
      console.error('Timesheet submission error:', err);
      setError(err.message || 'Failed to submit timesheet. Please try again.');
      message.error(err.message || 'Failed to submit timesheet');
    } finally {
      setSubmitting(false);
    }
  };

  // Clear form
  const handleClearForm = () => {
    form.resetFields();
    form.setFieldsValue({
      employeeName: user.name,
      employeeEmail: user.email,
      date: dayjs(),
    });
    setTasks([]);
    setSelectedProject(null);
    setError(null);
  };

  // Disable future dates
  const disabledDate = (current) => {
    return current && current > dayjs().endOf('day');
  };

  return (
    <Card className="shadow-lg max-w-4xl mx-auto">
      <div className="text-center mb-6">
        <Title level={2} className="text-gray-900 mb-2">
          <ClockCircleOutlined className="mr-2" />
          Submit Timesheet
        </Title>
        <Text className="text-gray-600">
          Welcome back, {user?.name}! Submit your daily work hours.
        </Text>
      </div>

      {error && (
        <Alert
          message="Submission Error"
          description={error}
          type="error"
          closable
          onClose={() => setError(null)}
          className="mb-4"
        />
      )}

      {loading ? (
        <div className="text-center py-8">
          <Spin size="large" />
          <div className="mt-4">
            <Text className="text-gray-500">Loading projects...</Text>
          </div>
        </div>
      ) : (
        <Form
          form={form}
          name="newTimesheet"
          onFinish={handleSubmit}
          layout="vertical"
          size="large"
          autoComplete="off"
          initialValues={{
            employeeName: user?.name || '',
            employeeEmail: user?.email || '',
            date: dayjs(),
          }}
        >
          {/* Employee Info */}
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item label="Employee Name" name="employeeName">
                <Input disabled className="bg-gray-50" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Employee Email" name="employeeEmail">
                <Input disabled className="bg-gray-50" />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          {/* Project and Date */}
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Project"
                name="project"
                rules={[{ required: true, message: 'Please select a project!' }]}
              >
                <Select
                  placeholder="Select a project"
                  showSearch
                  filterOption={(input, option) =>
                    (option.label ?? '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  onChange={handleProjectChange}
                  disabled={projects.length === 0}
                >
                  {projects
                    .filter(project => project.status !== 'completed' && project.status !== 'cancelled')
                    .map((project) => (
                    <Option key={project._id} value={project._id} label={project.name}>
                      <ProjectOutlined className="mr-2" />
                      {project.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Date"
                name="date"
                rules={[{ required: true, message: 'Please select a date!' }]}
              >
                <DatePicker
                  className="w-full"
                  format="YYYY-MM-DD"
                  disabledDate={disabledDate}
                  suffixIcon={<CalendarOutlined />}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Task (Optional) */}
          <Row gutter={[16, 0]}>
            <Col xs={24}>
              <Form.Item label="Task (Optional)" name="task">
                <Select
                  placeholder={selectedProject ? "Select a task (optional)" : "Select a project first"}
                  disabled={!selectedProject || tasks.length === 0}
                  showSearch
                  allowClear
                  filterOption={(input, option) =>
                    (option.label ?? '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {tasks.map((task) => (
                    <Option key={task._id} value={task._id} label={task.title}>
                      <CheckSquareOutlined className="mr-2" />
                      {task.title}
                      {task.priority && (
                        <span className={`ml-2 text-xs ${
                          task.priority === 'high' ? 'text-red-500' : 
                          task.priority === 'medium' ? 'text-yellow-500' : 'text-green-500'
                        }`}>
                          ({task.priority})
                        </span>
                      )}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Hours and Description */}
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Hours Worked"
                name="hours"
                rules={[
                  { required: true, message: 'Please enter hours worked!' },
                  { type: 'number', min: 0.5, max: 24, message: 'Hours must be between 0.5 and 24!' },
                ]}
              >
                <InputNumber
                  className="w-full"
                  placeholder="Enter hours (e.g., 8.5)"
                  min={0.5}
                  max={24}
                  step={0.5}
                  precision={1}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Description (Optional)" name="description">
                <Input.TextArea
                  placeholder="Brief description of work done"
                  rows={1}
                  maxLength={500}
                  showCount
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          {/* Action Buttons */}
          <Form.Item className="mb-0">
            <Row gutter={[16, 16]} justify="end">
              <Col xs={24} sm={12} md={8}>
                <Button
                  type="default"
                  size="large"
                  className="w-full"
                  onClick={handleClearForm}
                  disabled={submitting}
                  icon={<ClearOutlined />}
                >
                  Clear Form
                </Button>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Button
                  type="primary"
                  size="large"
                  className="w-full"
                  htmlType="submit"
                  loading={submitting}
                  icon={<SaveOutlined />}
                >
                  Submit Timesheet
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      )}

      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <Text className="text-sm text-blue-800">
          <strong>Note:</strong> All timesheet entries are recorded in the company's system. 
          Make sure to submit your hours daily for accurate tracking.
        </Text>
      </div>
    </Card>
  );
};

export default NewTimesheetForm;
